import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  CUSTOMER_OWNED_REMOTE_NOTE,
  INTENDED_DEVICE_CLASS,
  SA_DEVICE_INTENT_OPTIONS,
  ZONE_NAME_PLACEHOLDER,
  applyDeviceTypeToAllZones,
  applyMondayToWeekdays,
  buildCreateBody,
  cloneHours,
  cloneZones,
  defaultOperatingHours,
  deviceClassShortLabel,
  emptyLocationDraft,
  emptyZoneDraft,
  findBatchStoreDuplicates,
  hoursSummary,
  isCustomerOwnedClass,
  isZoneReady,
  IANA_TIMEZONE_OPTIONS,
  validateLocationDraft,
  WEEKDAY_LABELS,
  WEEKDAYS,
  zonesSummary,
  type IntendedDeviceClass,
  type LocationDraft,
  type OperatingHours,
  type ZoneCreatePayload,
  type ZoneDraft,
} from "@/features/accounts/locationCreateHelpers";
import styles from "./LocationCreateWorkspace.module.scss";

export type BrandOption = { brandId: string; name: string };

type CreateFn = (args: {
  brandId: string;
  idempotencyKey: string;
  label?: string;
  storeNumber?: string;
  address: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  timezone: string;
  operatingHours: OperatingHours;
  zones: ZoneCreatePayload[];
  confirmDuplicateStoreNumber?: boolean;
}) => Promise<{ locationId?: string; idempotentReplay?: boolean }>;

interface Props {
  show: boolean;
  onHide: () => void;
  operatorName: string;
  brandOptions: BrandOption[];
  /** When set, Brand is fixed (MA). When omitted, SA selects Brand once. */
  fixedBrand?: BrandOption | null;
  createLocation: CreateFn;
  onCreated?: () => void;
}

function OperatingHoursEditor({
  hours,
  onChange,
  disabled,
}: {
  hours: OperatingHours;
  onChange: (h: OperatingHours) => void;
  disabled?: boolean;
}) {
  const setDay = (day: string, patch: Record<string, unknown>) => {
    const next = cloneHours(hours);
    const key = day as keyof OperatingHours;
    if (patch.open === false) {
      next[key] = { open: false };
    } else {
      next[key] = { ...(next[key] as object), ...patch } as OperatingHours[keyof OperatingHours];
    }
    onChange(next);
  };

  return (
    <div>
      <table className={styles.hoursTable}>
        <thead>
          <tr>
            <th>Day</th>
            <th>Status</th>
            <th>Open</th>
            <th>Close</th>
          </tr>
        </thead>
        <tbody>
          {WEEKDAYS.map((day) => {
            const entry = hours[day as keyof OperatingHours] || { open: false };
            const open = Boolean((entry as { open?: boolean }).open);
            return (
              <tr key={day}>
                <td>{WEEKDAY_LABELS[day]}</td>
                <td>
                  <Form.Select
                    size="sm"
                    value={open ? "open" : "closed"}
                    disabled={disabled}
                    onChange={(e) =>
                      setDay(
                        day,
                        e.target.value === "open"
                          ? {
                              open: true,
                              openTime: (entry as { openTime?: string }).openTime || "09:00",
                              closeTime: (entry as { closeTime?: string }).closeTime || "21:00",
                            }
                          : { open: false },
                      )
                    }
                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </Form.Select>
                </td>
                <td>
                  {open ? (
                    <Form.Control
                      type="time"
                      size="sm"
                      value={(entry as { openTime?: string }).openTime || "09:00"}
                      disabled={disabled}
                      onChange={(e) => setDay(day, { openTime: e.target.value })}
                    />
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {open ? (
                    <Form.Control
                      type="time"
                      size="sm"
                      value={(entry as { closeTime?: string }).closeTime || "21:00"}
                      disabled={disabled}
                      onChange={(e) => setDay(day, { closeTime: e.target.value })}
                    />
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={styles.hoursActions}>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={disabled}
          onClick={() => onChange(applyMondayToWeekdays(hours))}
        >
          Apply Monday to Weekdays
        </Button>
      </div>
    </div>
  );
}

function ZonesPlaybackEditor({
  zones,
  onChange,
  disabled,
  showApplyZonesToAllLocations,
  onApplyZonesToAllLocations,
}: {
  zones: ZoneDraft[];
  onChange: (zones: ZoneDraft[]) => void;
  disabled?: boolean;
  showApplyZonesToAllLocations?: boolean;
  onApplyZonesToAllLocations?: (zones: ZoneDraft[]) => void;
}) {
  const list = Array.isArray(zones) && zones.length ? zones : [emptyZoneDraft()];

  const updateZone = (clientZoneKey: string, patch: Partial<ZoneDraft>) => {
    onChange(
      list.map((z) => (z.clientZoneKey === clientZoneKey ? { ...z, ...patch } : z)),
    );
  };

  const addZone = () => {
    onChange([...list, emptyZoneDraft({ expanded: true })]);
  };

  const removeZone = (clientZoneKey: string) => {
    if (list.length <= 1) return;
    onChange(list.filter((z) => z.clientZoneKey !== clientZoneKey));
  };

  const applyDeviceType = () => {
    const source =
      list.find((z) => z.expanded)?.intendedDeviceClass ||
      list[0]?.intendedDeviceClass ||
      INTENDED_DEVICE_CLASS.PLAYER_11;
    onChange(applyDeviceTypeToAllZones(list, source));
  };

  return (
    <div className={styles.zones}>
      {list.map((zone, index) => {
        const ready = isZoneReady(zone);
        const customerOwned = isCustomerOwnedClass(zone.intendedDeviceClass);
        return (
          <div className={styles.zoneRow} key={zone.clientZoneKey}>
            <button
              type="button"
              className={styles.zoneSummary}
              onClick={() =>
                updateZone(zone.clientZoneKey, { expanded: !zone.expanded })
              }
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
              <span>{String(zone.name || "").trim() || "Zone name…"}</span>
              <span>{deviceClassShortLabel(zone.intendedDeviceClass)}</span>
              <span className={ready ? styles.zoneReady : styles.zonePending}>
                {ready ? "Ready" : "Name needed"}
              </span>
              <span>{zone.expanded ? "▾" : "▸"}</span>
            </button>
            {zone.expanded && (
              <div className={styles.zoneExpanded}>
                <div className={styles.grid}>
                  <div className={styles.field}>
                    <label>Zone Name *</label>
                    <Form.Control
                      size="sm"
                      value={zone.name}
                      disabled={disabled}
                      placeholder={ZONE_NAME_PLACEHOLDER}
                      onChange={(e) =>
                        updateZone(zone.clientZoneKey, { name: e.target.value })
                      }
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Device Intent *</label>
                    <Form.Select
                      size="sm"
                      value={zone.intendedDeviceClass || INTENDED_DEVICE_CLASS.PLAYER_11}
                      disabled={disabled}
                      onChange={(e) => {
                        const nextClass = e.target.value as IntendedDeviceClass;
                        updateZone(zone.clientZoneKey, {
                          intendedDeviceClass: nextClass,
                          intendedDeviceDescription: isCustomerOwnedClass(nextClass)
                            ? zone.intendedDeviceDescription || ""
                            : "",
                        });
                      }}
                    >
                      {SA_DEVICE_INTENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  {customerOwned && (
                    <div className={`${styles.field} ${styles.full}`}>
                      <label>Device Description (optional)</label>
                      <Form.Control
                        size="sm"
                        value={zone.intendedDeviceDescription || ""}
                        disabled={disabled}
                        placeholder="e.g. Samsung Galaxy Tab A9, model/notes"
                        onChange={(e) =>
                          updateZone(zone.clientZoneKey, {
                            intendedDeviceDescription: e.target.value,
                          })
                        }
                      />
                      <div className={styles.hint}>{CUSTOMER_OWNED_REMOTE_NOTE}</div>
                    </div>
                  )}
                </div>
                <div className={styles.zoneActions}>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={disabled || list.length <= 1}
                    onClick={() => removeZone(zone.clientZoneKey)}
                  >
                    Remove Zone
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div className={styles.zonesActions}>
        <Button size="sm" variant="outline-secondary" disabled={disabled} onClick={addZone}>
          + Add Zone
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={disabled || list.length < 2}
          onClick={applyDeviceType}
          title="Copies the expanded Zone's device type (or Zone 01) to every Zone"
        >
          Apply Device Type to All
        </Button>
        {showApplyZonesToAllLocations && (
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={disabled}
            onClick={() => onApplyZonesToAllLocations?.(list)}
          >
            Apply Zone Setup to All Locations
          </Button>
        )}
      </div>
    </div>
  );
}

function LocationFields({
  row,
  onChange,
  disabled,
  showBatchActions,
  onApplyTimezoneAll,
  onApplyHoursAll,
  onApplyZonesAll,
}: {
  row: LocationDraft;
  onChange: (row: LocationDraft) => void;
  disabled?: boolean;
  showBatchActions?: boolean;
  onApplyTimezoneAll?: (tz: string) => void;
  onApplyHoursAll?: (h: OperatingHours) => void;
  onApplyZonesAll?: (zones: ZoneDraft[]) => void;
}) {
  const set = (key: keyof LocationDraft, value: unknown) =>
    onChange({ ...row, [key]: value } as LocationDraft);

  return (
    <>
      <div className={styles.grid}>
        <div className={`${styles.field} ${styles.storeEmphasis}`}>
          <label>Store #</label>
          <Form.Control
            size="sm"
            value={row.storeNumber}
            disabled={disabled}
            placeholder="e.g. 1321"
            onChange={(e) => set("storeNumber", e.target.value)}
          />
          <div className={styles.hint}>Primary operational ID when used by Brand</div>
        </div>
        <div className={styles.field}>
          <label>Location Label (optional)</label>
          <Form.Control
            size="sm"
            value={row.label}
            disabled={disabled}
            placeholder="e.g. Downtown, Airport Terminal B"
            onChange={(e) => set("label", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>Address Line 1 *</label>
          <Form.Control
            size="sm"
            value={row.street}
            disabled={disabled}
            onChange={(e) => set("street", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>Address Line 2 / Suite / Unit</label>
          <Form.Control
            size="sm"
            value={row.street2}
            disabled={disabled}
            onChange={(e) => set("street2", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>City *</label>
          <Form.Control
            size="sm"
            value={row.city}
            disabled={disabled}
            onChange={(e) => set("city", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>State / Province *</label>
          <Form.Control
            size="sm"
            value={row.state}
            disabled={disabled}
            onChange={(e) => set("state", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>ZIP / Postal Code *</label>
          <Form.Control
            size="sm"
            value={row.zipCode}
            disabled={disabled}
            onChange={(e) => set("zipCode", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>Country *</label>
          <Form.Control
            size="sm"
            value={row.country}
            disabled={disabled}
            onChange={(e) => set("country", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>Timezone *</label>
          <Form.Select
            size="sm"
            value={row.timezone}
            disabled={disabled}
            onChange={(e) => set("timezone", e.target.value)}
          >
            {IANA_TIMEZONE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
          {showBatchActions && (
            <Button
              size="sm"
              variant="outline-secondary"
              className="mt-2"
              disabled={disabled}
              onClick={() => onApplyTimezoneAll?.(row.timezone)}
            >
              Apply timezone to all
            </Button>
          )}
        </div>
      </div>

      <div className={styles.section} style={{ marginTop: 12, marginBottom: 0 }}>
        <div className={styles.sectionHeader}>
          <span>Operating Hours</span>
          <span className={styles.muted}>{hoursSummary(row.operatingHours)}</span>
        </div>
        <div className={styles.sectionBody}>
          <OperatingHoursEditor
            hours={row.operatingHours || defaultOperatingHours()}
            disabled={disabled}
            onChange={(h) => set("operatingHours", h)}
          />
          {showBatchActions && (
            <div className={styles.hoursActions}>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={disabled}
                onClick={() => onApplyHoursAll?.(row.operatingHours)}
              >
                Apply hours to all Locations
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.section} style={{ marginTop: 12, marginBottom: 0 }}>
        <div className={styles.sectionHeader}>
          <span>Zones &amp; Playback</span>
          <span className={styles.muted}>{zonesSummary(row.zones)}</span>
        </div>
        <div className={styles.sectionBody}>
          <ZonesPlaybackEditor
            zones={row.zones}
            disabled={disabled}
            showApplyZonesToAllLocations={showBatchActions}
            onApplyZonesToAllLocations={onApplyZonesAll}
            onChange={(nextZones) => set("zones", nextZones)}
          />
        </div>
      </div>
    </>
  );
}

const LocationCreateWorkspace: React.FC<Props> = ({
  show,
  onHide,
  operatorName,
  brandOptions,
  fixedBrand = null,
  createLocation,
  onCreated,
}) => {
  const [mode, setMode] = useState<"single" | "multiple">("single");
  const [brandId, setBrandId] = useState(fixedBrand?.brandId || "");
  const [rows, setRows] = useState<LocationDraft[]>([emptyLocationDraft()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [globalWarning, setGlobalWarning] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setMode("single");
    setBrandId(fixedBrand?.brandId || "");
    setRows([emptyLocationDraft()]);
    setError(null);
    setGlobalWarning(null);
    setSubmitting(false);
  }, [show, fixedBrand?.brandId]);

  const selectedBrandName = useMemo(() => {
    if (fixedBrand?.name) return fixedBrand.name;
    const hit = brandOptions.find((b) => b.brandId === brandId);
    return hit?.name || "";
  }, [fixedBrand, brandOptions, brandId]);

  const summary = useMemo(() => {
    const total = rows.length;
    const created = rows.filter((r) => r.status === "created").length;
    const attention = rows.filter(
      (r) => r.status === "needs_attention" || r.status === "failed",
    ).length;
    return { total, created, attention, ready: total - created - attention };
  }, [rows]);

  const updateRow = (clientRowId: string, next: LocationDraft) => {
    setRows((prev) => prev.map((r) => (r.clientRowId === clientRowId ? next : r)));
  };

  const addRow = () => {
    const template = rows[0] || emptyLocationDraft();
    setRows((prev) => [
      ...prev,
      emptyLocationDraft({
        timezone: template.timezone,
        country: template.country,
        operatingHours: cloneHours(template.operatingHours),
        zones: cloneZones(template.zones),
      }),
    ]);
  };

  const applyZonesAll = (zones: ZoneDraft[]) => {
    setRows((prev) =>
      prev.map((r) =>
        r.status === "created"
          ? r
          : { ...r, zones: cloneZones(zones), status: "ready", statusMessage: null },
      ),
    );
  };

  const createOne = async (
    row: LocationDraft,
    { confirmDuplicate = false } = {},
  ): Promise<LocationDraft> => {
    const missing = validateLocationDraft(row);
    if (missing.length) {
      return {
        ...row,
        status: "needs_attention",
        statusMessage: `Missing: ${missing.join(", ")}`,
      };
    }
    if (!brandId) {
      return { ...row, status: "needs_attention", statusMessage: "Brand is required" };
    }
    try {
      const body = buildCreateBody(row, { confirmDuplicateStoreNumber: confirmDuplicate });
      const data = await createLocation({
        brandId,
        idempotencyKey: row.idempotencyKey,
        label: body.label,
        storeNumber: body.storeNumber,
        address: body.address,
        timezone: body.timezone,
        operatingHours: body.operatingHours,
        zones: body.zones,
        confirmDuplicateStoreNumber: body.confirmDuplicateStoreNumber,
      });
      return {
        ...row,
        status: "created",
        statusMessage: data?.idempotentReplay ? "Already created" : "Created",
        locationId: data?.locationId || null,
        confirmDuplicateStoreNumber: false,
      };
    } catch (err: unknown) {
      const e = err as {
        status?: number;
        data?: { message?: string; code?: string; details?: { code?: string } };
        message?: string;
      };
      const code = e?.data?.code || e?.data?.details?.code;
      const message = e?.data?.message || e?.message || "Create failed";
      if (e?.status === 409 || code === "STORE_NUMBER_DUPLICATE_WARNING") {
        return {
          ...row,
          status: "needs_attention",
          statusMessage: message,
          confirmDuplicateStoreNumber: true,
        };
      }
      return { ...row, status: "failed", statusMessage: message };
    }
  };

  const submit = async () => {
    if (!brandId) {
      setError("Select a Brand for this create session");
      return;
    }
    setSubmitting(true);
    setError(null);
    setGlobalWarning(null);

    const pending = rows.filter((r) => r.status !== "created");
    const batchDupes = findBatchStoreDuplicates(pending);
    if (batchDupes.size > 0) {
      setRows((prev) =>
        prev.map((r) =>
          batchDupes.has(r.clientRowId)
            ? {
                ...r,
                status: "needs_attention",
                statusMessage: "Duplicate Store # within this batch",
              }
            : r,
        ),
      );
      setGlobalWarning("Resolve duplicate Store # values within this batch before creating.");
      setSubmitting(false);
      return;
    }

    const nextRows = [...rows];
    let anyCreated = false;
    for (let i = 0; i < nextRows.length; i += 1) {
      if (nextRows[i].status === "created") continue;
      // eslint-disable-next-line no-await-in-loop
      const updated = await createOne(nextRows[i], {
        confirmDuplicate: Boolean(nextRows[i].confirmDuplicateStoreNumber),
      });
      nextRows[i] = updated;
      setRows([...nextRows]);
      if (updated.status === "created") anyCreated = true;
    }
    setSubmitting(false);
    if (anyCreated) onCreated?.();
    if (nextRows.every((r) => r.status === "created")) onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      dialogClassName={styles.dialog}
      centered={false}
    >
      <Modal.Header closeButton className={styles.header}>
        <Modal.Title className={styles.title}>
          Add Location{mode === "multiple" ? "s" : ""} for {operatorName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <div className={styles.modeToggle} role="group" aria-label="Add mode">
          <button
            type="button"
            className={mode === "single" ? styles.modeActive : undefined}
            onClick={() => {
              setMode("single");
              setRows((prev) => [prev[0] || emptyLocationDraft()]);
            }}
          >
            Single Location
          </button>
          <button
            type="button"
            className={mode === "multiple" ? styles.modeActive : undefined}
            onClick={() => {
              setMode("multiple");
              setRows((prev) => {
                if (prev.length >= 2) return prev;
                const base = prev[0] || emptyLocationDraft();
                return [
                  base,
                  emptyLocationDraft({
                    timezone: base.timezone,
                    country: base.country,
                    operatingHours: cloneHours(base.operatingHours),
                    zones: cloneZones(base.zones),
                  }),
                  emptyLocationDraft({
                    timezone: base.timezone,
                    country: base.country,
                    operatingHours: cloneHours(base.operatingHours),
                    zones: cloneZones(base.zones),
                  }),
                ];
              });
            }}
          >
            Multiple Locations
          </button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {globalWarning && <Alert variant="warning">{globalWarning}</Alert>}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Location Context</div>
          <div className={styles.sectionBody}>
            <p className={styles.contextLine}>
              Operator: <strong>{operatorName}</strong>
            </p>
            {fixedBrand ? (
              <p className={styles.contextLine}>
                Brand: <strong>{fixedBrand.name || "—"}</strong>
              </p>
            ) : (
              <Form.Group className="mb-0" style={{ maxWidth: 360 }}>
                <Form.Label className={styles.fieldLabel}>Brand *</Form.Label>
                <Form.Select
                  size="sm"
                  value={brandId}
                  disabled={submitting}
                  onChange={(e) => setBrandId(e.target.value)}
                  data-testid="sa-location-brand"
                >
                  <option value="">Select Brand</option>
                  {brandOptions.map((b) => (
                    <option key={b.brandId} value={b.brandId}>
                      {b.name}
                    </option>
                  ))}
                </Form.Select>
                {brandOptions.length === 0 && (
                  <Form.Text muted>
                    This Client has no active Brand enrollment. Assign a Brand
                    before creating Locations.
                  </Form.Text>
                )}
              </Form.Group>
            )}
            <p className={styles.hint}>
              Brand owns each Location. {operatorName} is assigned as Operator
              {selectedBrandName ? ` under ${selectedBrandName}` : ""}.
            </p>
          </div>
        </div>

        {mode === "multiple" && (
          <div className={styles.batchSummary}>
            <span>
              <strong>{summary.total}</strong> Locations
            </span>
            <span>
              <strong>{summary.ready}</strong> Ready
            </span>
            <span>
              <strong>{summary.attention}</strong> Need attention
            </span>
            <span>
              <strong>{summary.created}</strong> Created
            </span>
            <Button size="sm" variant="outline-secondary" disabled={submitting} onClick={addRow}>
              + Add another Location
            </Button>
          </div>
        )}

        {mode === "single" ? (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>Location Information</div>
            <div className={styles.sectionBody}>
              <LocationFields
                row={rows[0]}
                disabled={submitting || rows[0]?.status === "created"}
                onChange={(next) =>
                  updateRow(rows[0].clientRowId, {
                    ...next,
                    status: "ready",
                    statusMessage: null,
                  })
                }
              />
              {rows[0]?.statusMessage && (
                <Alert
                  variant={
                    rows[0].status === "needs_attention" || rows[0].status === "failed"
                      ? "warning"
                      : "info"
                  }
                  className="mt-2 mb-0"
                >
                  {rows[0].statusMessage}
                </Alert>
              )}
            </div>
          </div>
        ) : (
          rows.map((row, index) => {
            const tzLabel =
              IANA_TIMEZONE_OPTIONS.find((o) => o.value === row.timezone)?.label ||
              row.timezone;
            return (
              <div className={styles.row} key={row.clientRowId}>
                <button
                  type="button"
                  className={styles.rowSummary}
                  onClick={() => updateRow(row.clientRowId, { ...row, expanded: !row.expanded })}
                >
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <span>{row.storeNumber ? `#${row.storeNumber}` : "No Store #"}</span>
                  <span>{row.street || "Address…"}</span>
                  <span>
                    {[row.city, row.state].filter(Boolean).join(", ") || "City / State"}
                  </span>
                  <span>{tzLabel}</span>
                  <span>{zonesSummary(row.zones)}</span>
                  <span>{hoursSummary(row.operatingHours)}</span>
                  <span className={styles[`status_${row.status}`] || styles.status_ready}>
                    {row.status === "needs_attention" ? "Attention" : row.status}
                  </span>
                  <span>{row.expanded ? "▾" : "▸"}</span>
                </button>
                {row.expanded && (
                  <div className={styles.rowExpanded}>
                    {row.statusMessage && (
                      <Alert variant="warning" className="py-2">
                        {row.statusMessage}
                      </Alert>
                    )}
                    <LocationFields
                      row={row}
                      disabled={submitting || row.status === "created"}
                      showBatchActions
                      onApplyTimezoneAll={(tz) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.status === "created" ? r : { ...r, timezone: tz },
                          ),
                        )
                      }
                      onApplyHoursAll={(h) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.status === "created"
                              ? r
                              : { ...r, operatingHours: cloneHours(h) },
                          ),
                        )
                      }
                      onApplyZonesAll={applyZonesAll}
                      onChange={(next) =>
                        updateRow(row.clientRowId, {
                          ...next,
                          status: next.status === "created" ? "created" : "ready",
                          statusMessage:
                            next.status === "created" ? next.statusMessage : null,
                        })
                      }
                    />
                    <div className={styles.rowActions}>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={submitting || rows.length <= 1 || row.status === "created"}
                        onClick={() =>
                          setRows((prev) => prev.filter((r) => r.clientRowId !== row.clientRowId))
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={submitting} onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" disabled={submitting || !brandId} onClick={() => submit()}>
          {submitting ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" /> Creating…
            </>
          ) : mode === "multiple" ? (
            "Create Locations"
          ) : (
            "Create Location"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LocationCreateWorkspace;
